import { Route, Routes, useParams } from "react-router";

import { getBreadcrumbsBackUrl } from "components/Breadcrumbs/helpers";
import useBreadcrumbs from "components/Breadcrumbs/useBreadcrumbs";
import NotFoundPage from "components/error/NotFoundPage";
import useTypedContext from "hooks/useTypedContext";
import { SpacesContext } from "views/Account/SpacesProvider";

import { FILTERS_ORDER_SETTINGS_KEY } from "../Spaces/constants";
import SpaceDetailsApiKeys from "./ApiKeys";
import SpaceDetailsApiKeySessions from "./ApiKeySessions";
import SpaceDetailsAccessGate from "./components/AccessGate";
import SpaceDetailsContexts from "./Contexts";
import SpaceDetailsHeader from "./Header";
import SpaceDetailsIdpGroups from "./IdpGroups";
import SpaceDetailsIdpGroupsLoginPolicyEmptyState from "./IdpGroups/LoginPolicyEmptyState";
import SpaceDetailsPolicies from "./Policies";
import SpaceDetailsStacks from "./Stacks";
import useShouldShowSpaceAccessForUserSessions from "./useShouldShowSpaceAccessForUserSessions";
import SpaceDetailsUsers from "./Users";
import SpaceDetailsUserSessions from "./UserSessions";

const SpaceDetails = () => {
  const { spaceId } = useParams<{ spaceId: string }>();
  const { spaces, hasRootSpaceAccess } = useTypedContext(SpacesContext);
  const isAccountUsingLoginPoliciesForSpaceAccess = useShouldShowSpaceAccessForUserSessions();

  const space = spaces.find((item) => item.id === spaceId);

  useBreadcrumbs(
    [
      {
        title: "Spaces",
        link: getBreadcrumbsBackUrl("/spaces", FILTERS_ORDER_SETTINGS_KEY),
      },
      ...(space ? [{ title: space.name }] : []),
    ],
    [spaceId, space?.name]
  );

  if (!space || !spaceId) {
    return <NotFoundPage />;
  }

  const spaceUrl = `/spaces/${spaceId}`;

  return (
    <>
      <SpaceDetailsHeader space={space} spaceUrl={spaceUrl} isRootAdmin={hasRootSpaceAccess} />
      <Routes>
        <Route element={<SpaceDetailsAccessGate spaceId={spaceId} requiredAccess="read" />}>
          <Route index element={<SpaceDetailsStacks spaceId={spaceId} />} />
          <Route path="contexts" element={<SpaceDetailsContexts spaceId={spaceId} />} />
          <Route path="policies" element={<SpaceDetailsPolicies spaceId={spaceId} />} />
        </Route>
        <Route element={<SpaceDetailsAccessGate spaceId={spaceId} requiredAccess="admin" />}>
          <Route
            path="users"
            element={
              isAccountUsingLoginPoliciesForSpaceAccess ? (
                <SpaceDetailsUserSessions spaceId={spaceId} />
              ) : (
                <SpaceDetailsUsers spaceId={spaceId} />
              )
            }
          />
          <Route
            path="idp-groups"
            element={
              isAccountUsingLoginPoliciesForSpaceAccess ? (
                <SpaceDetailsIdpGroupsLoginPolicyEmptyState />
              ) : (
                <SpaceDetailsIdpGroups spaceId={spaceId} />
              )
            }
          />
          <Route
            path="api-keys"
            element={
              isAccountUsingLoginPoliciesForSpaceAccess ? (
                <SpaceDetailsApiKeySessions spaceId={spaceId} />
              ) : (
                <SpaceDetailsApiKeys spaceId={spaceId} />
              )
            }
          />
        </Route>
        <Route path="*" element={<NotFoundPage />} />
      </Routes>
    </>
  );
};

export default SpaceDetails;
